import { createSlice } from "@reduxjs/toolkit";
import {
  getAllNurse,
  postNurse,
  getNurseDetails,
  getAllServices,
  getSubServices,
  getServiceItems,
  editNurse,
  deleteNurse,
  createService,
  createSubService,
  toggleNurseActiveStatus,
} from "./ManageNurseThunk";

// Initial state for the slice
const initialState = {
  nurses: [],
  services: [],
  subServices: [],
  serviceItems: [],
  getAllNurseSuccess: null,
  getAllNurseErr: null,
  postNurseSuccess: null,
  postNurseErr: null,
  getNurseDetailsSuccess: null,
  getNurseDetailsErr: null,
  getAllServicesSuccess: null,
  getAllServicesErr: null,
  getSubServicesSuccess: null,
  getSubServicesErr: null,
  getServiceItemsSuccess: null,
  getServiceItemsErr: null,
  editNurseSuccess: null,
  editNurseErr: null,
  loading: false,
  createServiceSuccess: null,
  createServiceErr: null,
  toggleStatusSuccess: null,
  toggleStatusErr: null,
  totalPages: 1,
  currentPage: 1,
};

// Create the slice
const manageNurseSlice = createSlice({
  name: "manageNurse",
  initialState,
  reducers: {
    resetGetAllNurse(state) {
      state.getAllNurseSuccess = null;
      state.getAllNurseErr = null;
    },
    resetPostNurse(state) {
      state.postNurseSuccess = null;
      state.postNurseErr = null;
    },
    resetGetNurseDetails(state) {
      state.getNurseDetailsSuccess = null;
      state.getNurseDetailsErr = null;
    },
    resetGetAllServices(state) {
      state.getAllServicesSuccess = null;
      state.getAllServicesErr = null;
    },
    resetGetSubServices(state) {
      state.getSubServicesSuccess = null;
      state.getSubServicesErr = null;
    },
    resetGetServiceItems(state) {
      state.getServiceItemsSuccess = null;
      state.getServiceItemsErr = null;
    },
    resetEditNurse(state) {
      state.editNurseSuccess = null;
      state.editNurseErr = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL NURSES
      .addCase(getAllNurse.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllNurse.fulfilled, (state, action) => {
        state.loading = false;
        state.getAllNurseSuccess = action.payload;
        state.nurses = action.payload?.data?.nurses || [];
      })
      .addCase(getAllNurse.rejected, (state, action) => {
        state.loading = false;
        state.getAllNurseErr = action.error.message;
      })

      .addCase(createService.fulfilled, (state, action) => {
        state.createServiceSuccess = action.payload;

        const newService = action.payload?.data?.service;

        if (newService) {
          state.services = [newService, ...state.services];
        }
      })
      .addCase(createService.rejected, (state, action) => {
        state.createServiceErr = action.payload;
      })

      .addCase(createSubService.fulfilled, (state, action) => {
        const newSubService = action.payload?.data?.subService;

        if (newSubService) {
          state.subServices = [newSubService, ...state.subServices];
        }
      })

      // POST NURSE
      .addCase(postNurse.pending, (state) => {
        state.loading = true;
      })
      .addCase(postNurse.fulfilled, (state, action) => {
        state.postNurseSuccess = action.payload;
      })
      .addCase(postNurse.rejected, (state, action) => {
        state.postNurseErr = action.payload;
      })

      // GET SINGLE NURSE DETAILS
      .addCase(getNurseDetails.fulfilled, (state, action) => {
        state.getNurseDetailsSuccess = action.payload;
      })
      .addCase(getNurseDetails.rejected, (state, action) => {
        state.getNurseDetailsErr = action.error.message;
      })
      .addCase(toggleNurseActiveStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleNurseActiveStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.toggleStatusSuccess = action.payload;

        const updatedNurse = action.payload?.data?.nurse;

        // Only update the nurses list — do NOT overwrite getNurseDetailsSuccess here.
        // The toggle response returns an unpopulated nurse (no service/subService refs),
        // which would break the edit form's specialization dropdowns.
        if (updatedNurse && Array.isArray(state.nurses)) {
          state.nurses = state.nurses.map((n) => (n._id === updatedNurse._id ? updatedNurse : n));
        }
      })
      .addCase(toggleNurseActiveStatus.rejected, (state, action) => {
        state.loading = false;
        state.toggleStatusErr = action.payload;
      })

      // GET ALL SERVICES
      .addCase(getAllServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllServices.fulfilled, (state, action) => {
        state.loading = false;
        state.getAllServicesSuccess = action.payload;

        state.services = action.payload?.data?.services || [];

        state.totalPages = action.payload?.data?.totalPages || 0;
        state.currentPage = action.payload?.data?.currentPage || 1;
      })

      .addCase(getAllServices.rejected, (state, action) => {
        state.loading = false;
        state.getAllServicesErr = action.payload;
      })

      // GET SUB-SERVICES
      .addCase(getSubServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubServices.fulfilled, (state, action) => {
        state.loading = false;
        state.getSubServicesSuccess = action.payload;
        state.subServices = action.payload?.data?.subServices || [];
      })
      .addCase(getSubServices.rejected, (state, action) => {
        state.loading = false;
        state.getSubServicesErr = action.payload;
      })

      // GET SERVICE ITEMS
      .addCase(getServiceItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceItems.fulfilled, (state, action) => {
        state.loading = false;
        state.serviceItems = action.payload?.data?.serviceItems || [];
        state.totalPages = action.payload?.data?.totalPages || 1;
        state.currentPage = action.payload?.data?.currentPage || 1;
      })

      .addCase(getServiceItems.rejected, (state, action) => {
        state.loading = false;
        state.getServiceItemsErr = action.payload;
      })
      // EDIT NURSE
      .addCase(editNurse.pending, (state) => {
        state.loading = true;
      })
      .addCase(editNurse.fulfilled, (state, action) => {
        state.loading = false;
        state.editNurseSuccess = action.payload;
      })
      .addCase(editNurse.rejected, (state, action) => {
        state.loading = false;
        state.editNurseErr = action.payload;
      })
      // DELETE NURSE
      .addCase(deleteNurse.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteNurse.fulfilled, (state, action) => {
        state.loading = false;

        if (Array.isArray(state.nurses)) {
          state.nurses = state.nurses.filter((nurse) => nurse._id !== action.payload);
        }
      })
      .addCase(deleteNurse.rejected, (state, action) => {
        state.loading = false;
        state.deleteNurseErr = action.payload;
      });
  },
});

// Export actions for resetting state
export const { resetGetAllNurse, resetPostNurse, resetGetNurseDetails, resetGetAllServices, resetGetSubServices, resetGetServiceItems, resetEditNurse } = manageNurseSlice.actions;

// Export the reducer
export default manageNurseSlice.reducer;
